import {
  getSpaPackageConfigGroups,
  getSpaPackageConfigGroupsKey,
} from '@features/spa/hooks/useGetSpaPackageConfigGroups';
import {
  getSpaPackageTimeslots,
  getSpaPackageTimeslotsKey,
} from '@features/spa/hooks/useGetSpaPackageTimeslots';
import { isSpaPackageUnavailable } from '@features/packages/utils/isSpaPackageUnavailable';
import { timeBandWindow } from '@features/DynamicPackagesConfigurationWizard/helpers/spaTimeBands';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { TimeSlotsType, ConfigurationGroupType } from 'shared-types/SpaPackage';

interface UseCheckSpaDayIsAvailableProps {
  propertyId: number;
  spaPackageId: number;
  datesToCheck: string[];
  numberOfGuests: number;
  enabled?: boolean;
}

interface UseCheckSpaDayIsAvailableReturn {
  availableDates: string[];
  isLoading: boolean;
  isFetching: boolean;
}

export const useCheckSpaDayIsAvailable = ({
  propertyId,
  spaPackageId,
  datesToCheck,
  numberOfGuests,
  enabled = true,
}: UseCheckSpaDayIsAvailableProps): UseCheckSpaDayIsAvailableReturn => {
  if (datesToCheck.length === 0) {
    return {
      availableDates: [],
      isLoading: false,
      isFetching: false,
    };
  }
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: [
      'checkSpaDayAvailability',
      propertyId,
      spaPackageId,
      datesToCheck,
      numberOfGuests,
      datesToCheck[0],
    ],
    queryFn: async () => {
      // Check cache for timeslots first, otherwise fetch
      const firstDate = datesToCheck[0];
      const timeslotsKey = getSpaPackageTimeslotsKey(
        propertyId,
        spaPackageId,
        firstDate,
        numberOfGuests,
        datesToCheck.length
      );
      let timeslots: TimeSlotsType | undefined = queryClient.getQueryData(timeslotsKey);

      if (!timeslots) {
        timeslots = await getSpaPackageTimeslots({
          spaPackageId,
          startDate: firstDate,
          quantity: numberOfGuests,
          propertyId,
          numberOfDays: datesToCheck.length,
        });
      }

      // Check each date for availability
      const availabilityChecks = await Promise.all(
        datesToCheck.map(async (date) => {
          // Check time band availability
          const hasTimeslots = timeslots?.[date] && timeslots?.[date].length > 0;
          if (!hasTimeslots) {
            return { date, isAvailable: false };
          }

          const dayWindow = timeBandWindow({ [date]: timeslots?.[date] });
          if (!dayWindow) {
            return { date, isAvailable: false };
          }

          // Check cache for config groups first
          const configGroupsKey = getSpaPackageConfigGroupsKey(
            propertyId,
            spaPackageId,
            date,
            numberOfGuests,
            dayWindow.startTime,
            dayWindow.endTime
          );
          let configGroups: ConfigurationGroupType[] | undefined =
            queryClient.getQueryData(configGroupsKey);

          if (!configGroups) {
            try {
              configGroups = await getSpaPackageConfigGroups(
                propertyId,
                spaPackageId,
                date,
                numberOfGuests,
                dayWindow.startTime,
                dayWindow.endTime
              );
            } catch (error) {
              return { date, isAvailable: false };
            }
          }

          // Check if config groups have enough availability
          if (!configGroups) {
            return { date, isAvailable: false };
          }

          const isConfigGroupUnavailable = isSpaPackageUnavailable(configGroups, numberOfGuests);

          return {
            date,
            isAvailable: !isConfigGroupUnavailable,
          };
        })
      );

      const availableDates = availabilityChecks
        .filter((check) => check.isAvailable)
        .map((check) => check.date);

      return { availableDates };
    },
    staleTime: 30000, // 30 seconds
    cacheTime: 30000, // 30 seconds
    enabled: enabled && !!spaPackageId && datesToCheck.length > 0,
  });

  return {
    availableDates: query.data?.availableDates || [],
    isLoading: query.isLoading,
    isFetching: query.isFetching,
  };
};
